import React, { useEffect, useMemo, useState } from 'react';
import EngagerSliderRow from './calc_components/EngagerSliderRow';
import CalculatorToolsRail from '../components/CalculatorToolsRail';
import { inrAmountInWordsShort } from './calc_components/calculatorAmountWords';
import CalculatorWhatThisMeans from './calc_components/CalculatorWhatThisMeans';
import CalculatorPrecisionToggle from './calc_components/CalculatorPrecisionToggle';
import { useCalcAmountDisplayMode } from './calc_components/useCalcAmountDisplayMode';
import { formatYearsCount } from './calc_components/calculatorFormatters';
import { computeEmi } from './EmiCalculator';
import './calculators.css';
import './EmiInputBands.css';
import './PartPrepaymentCalculator.css';

const formatterINR = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const minInterestRate = 5;
const maxInterestRate = 20;
const interestRateStep = 0.25;

const minTenureYears = 5;
const maxTenureYears = 30;

const minPrepayYear = 1;
const maxPrepayYearCap = 15;

const minLoanAmount = 0;
const maxLoanAmount = 1000000000;
const minPrepayAmount = 0;

const STANDALONE_DEFAULTS = {
  loanAmount: 10000000,
  interestRate: 9,
  tenureYears: 20,
  prepayAmount: 500000,
  prepayYear: 1,
};

/** Article examples use a ₹1 lakh prepayment. */
const ARTICLE_EMBED_DEFAULTS = {
  ...STANDALONE_DEFAULTS,
  prepayAmount: 100000,
};

function getDefaults(embed) {
  return embed ? ARTICLE_EMBED_DEFAULTS : STANDALONE_DEFAULTS;
}

function formatInterestRatePct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(2)}%`;
}

function snapInterestRate(n) {
  const raw = Number(n);
  if (!Number.isFinite(raw)) return STANDALONE_DEFAULTS.interestRate;
  const snapped =
    minInterestRate +
    Math.round((raw - minInterestRate) / interestRateStep) * interestRateStep;
  return Math.min(
    maxInterestRate,
    Math.max(minInterestRate, Number(snapped.toFixed(2))),
  );
}

function formatPrepayYear(v) {
  const n = Math.round(Number(v));
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n === 1) return 'End of 1st year';
  if (n === 2) return 'End of 2nd year';
  if (n === 3) return 'End of 3rd year';
  return `End of ${n}th year`;
}

function formatMonthsReduced(months) {
  const n = Math.round(Number(months));
  if (!Number.isFinite(n) || n <= 0) return '0 months';
  return n === 1 ? '1 month' : `${n} months`;
}

/** Outstanding balance after `months` EMIs on a fixed-rate amortising loan. */
export function balanceAfterMonths(loanAmount, annualInterestRatePct, monthlyEmi, months) {
  const P = Number(loanAmount);
  const emi = Number(monthlyEmi);
  const k = Math.max(0, Math.round(Number(months)));
  if (!(P > 0) || !(emi > 0) || k <= 0) return Math.max(0, P);

  const annualRate = Number(annualInterestRatePct);
  if (!Number.isFinite(annualRate) || annualRate <= 0) {
    return Math.max(0, P - emi * k);
  }

  const r = annualRate / 12 / 100;
  const growth = (1 + r) ** k;
  return Math.max(0, P * growth - (emi * (growth - 1)) / r);
}

/** Months to clear balance at fixed EMI (null if EMI cannot cover interest). */
export function tenureMonthsFromEmi(loanAmount, monthlyEmi, annualInterestRatePct) {
  const P = Number(loanAmount);
  const emi = Number(monthlyEmi);
  if (!(P > 0) || !(emi > 0)) return null;

  const annualRate = Number(annualInterestRatePct);
  if (!Number.isFinite(annualRate) || annualRate <= 0) {
    return P / emi;
  }

  const r = annualRate / 12 / 100;
  if (emi <= P * r * (1 + 1e-10)) return null;

  const n = Math.log(emi / (emi - P * r)) / Math.log(1 + r);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

/**
 * Part-prepayment with EMI held constant: tenure cut (months) + EMI.
 * Prepayment is applied at the end of `prepayYear` (after that many years of EMIs).
 */
export function computePartPrepaymentImpact({
  loanAmount,
  interestRate,
  tenureYears,
  prepayAmount,
  prepayYear,
}) {
  const P = Number(loanAmount);
  const rate = Number(interestRate);
  const years = Math.round(Number(tenureYears));
  const prepay = Math.max(0, Number(prepayAmount) || 0);
  const year = Math.round(Number(prepayYear));

  const empty = {
    monthlyEmi: 0,
    monthsReduced: 0,
    balanceBeforePrepay: 0,
    balanceAfterPrepay: 0,
    remainingMonthsWithoutPrepay: 0,
    remainingMonthsWithPrepay: 0,
    feasible: false,
  };

  if (!(P > 0) || !(years > 0) || !(year >= 1) || year >= years) {
    return empty;
  }

  const monthlyEmi = computeEmi(P, rate, years);
  if (!(monthlyEmi > 0)) return empty;

  const totalMonths = years * 12;
  const monthsElapsed = year * 12;
  const balanceBeforePrepay = balanceAfterMonths(P, rate, monthlyEmi, monthsElapsed);
  const balanceAfterPrepay = Math.max(0, balanceBeforePrepay - prepay);
  const remainingMonthsWithoutPrepay = totalMonths - monthsElapsed;

  if (balanceAfterPrepay < 1) {
    return {
      monthlyEmi,
      monthsReduced: Math.max(0, Math.round(remainingMonthsWithoutPrepay)),
      balanceBeforePrepay,
      balanceAfterPrepay: 0,
      remainingMonthsWithoutPrepay,
      remainingMonthsWithPrepay: 0,
      feasible: true,
    };
  }

  const remainingWithPrepay = tenureMonthsFromEmi(balanceAfterPrepay, monthlyEmi, rate);
  if (remainingWithPrepay == null) {
    return {
      ...empty,
      monthlyEmi,
      balanceBeforePrepay,
      balanceAfterPrepay,
      remainingMonthsWithoutPrepay,
      feasible: false,
    };
  }

  const monthsReduced = Math.max(
    0,
    Math.round(remainingMonthsWithoutPrepay - remainingWithPrepay),
  );

  return {
    monthlyEmi,
    monthsReduced,
    balanceBeforePrepay,
    balanceAfterPrepay,
    remainingMonthsWithoutPrepay,
    remainingMonthsWithPrepay: remainingWithPrepay,
    feasible: true,
  };
}

export default function PartPrepaymentCalculator({ embed = false }) {
  useEffect(() => {
    if (!embed) window.scrollTo(0, 0);
  }, [embed]);

  const defaults = getDefaults(embed);
  const [loanAmount, setLoanAmount] = useState(defaults.loanAmount);
  const [loanAmountForWords, setLoanAmountForWords] = useState(defaults.loanAmount);
  const [interestRate, setInterestRate] = useState(defaults.interestRate);
  const [tenureYears, setTenureYears] = useState(defaults.tenureYears);
  const [prepayAmount, setPrepayAmount] = useState(defaults.prepayAmount);
  const [prepayAmountForWords, setPrepayAmountForWords] = useState(defaults.prepayAmount);
  const [prepayYear, setPrepayYear] = useState(defaults.prepayYear);

  const maxPrepayYear = Math.max(
    minPrepayYear,
    Math.min(maxPrepayYearCap, tenureYears - 1),
  );

  useEffect(() => {
    setPrepayYear((prev) => Math.min(Math.max(minPrepayYear, prev), maxPrepayYear));
  }, [maxPrepayYear]);

  const impact = useMemo(
    () =>
      computePartPrepaymentImpact({
        loanAmount,
        interestRate,
        tenureYears,
        prepayAmount: Math.min(prepayAmount, loanAmount),
        prepayYear: Math.min(prepayYear, maxPrepayYear),
      }),
    [loanAmount, interestRate, tenureYears, prepayAmount, prepayYear, maxPrepayYear],
  );

  const loanAmountWords =
    !embed && loanAmountForWords > 0
      ? inrAmountInWordsShort(Math.round(loanAmountForWords))
      : '';
  const prepayAmountWords =
    !embed && prepayAmountForWords > 0
      ? inrAmountInWordsShort(Math.round(prepayAmountForWords))
      : '';

  function handleStartOver(e) {
    e.preventDefault();
    const d = getDefaults(embed);
    setLoanAmount(d.loanAmount);
    setLoanAmountForWords(d.loanAmount);
    setInterestRate(d.interestRate);
    setTenureYears(d.tenureYears);
    setPrepayAmount(d.prepayAmount);
    setPrepayAmountForWords(d.prepayAmount);
    setPrepayYear(d.prepayYear);
  }

  const calculatorCard = (
    <div
      className={`appContainer nps-ups--compact emi-calculator part-prepayment-calculator${
        embed ? ' nps-ups--embed' : ''
      }`}
    >
      <h1 className="tool-heading">Part-Prepayment Tenure Calculator</h1>

      {!embed ? (
        <div className="col-light-grey italic font-size-small nps-ups-intro">
          <p>
            See how many months a part-prepayment can cut from your loan when you keep paying the
            same EMI.
          </p>
        </div>
      ) : null}

      <PartPrepaymentResultPanel embed={embed} impact={impact} prepayYear={prepayYear} />

      <div className="border-top-light nps-ups-divider" />

      <section className="emi-input-band emi-input-band--b">
        <div className="savings-goal-first-year-exp-field">
          <EngagerSliderRow
            id="pp-loan-amount"
            label="Loan amount"
            tooltip="Original loan principal at the start of the tenure."
            value={Math.min(loanAmount, maxLoanAmount)}
            setValue={(v) => {
              const next = Math.min(v, maxLoanAmount);
              setLoanAmount(next);
              setLoanAmountForWords(next);
              setPrepayAmount((p) => Math.min(p, next));
              setPrepayAmountForWords((p) => Math.min(p, next));
            }}
            onLiveValueChange={(n) => {
              if (n != null && Number.isFinite(n)) {
                setLoanAmountForWords(Math.min(maxLoanAmount, Math.max(0, n)));
              } else {
                setLoanAmountForWords(0);
              }
            }}
            min={minLoanAmount}
            max={maxLoanAmount}
            step={100000}
            format={(v) => formatterINR.format(v)}
            variant="inr"
            defaultValue={defaults.loanAmount}
          />
          {loanAmountWords ? (
            <p className="calc-output-amount-in-words savings-goal-first-year-exp-words">
              {loanAmountWords}
            </p>
          ) : null}
        </div>
      </section>

      <section className="emi-input-band emi-input-band--b">
        <EngagerSliderRow
          id="pp-interest-rate"
          label="Interest rate"
          tooltip="Annual interest rate on the loan."
          value={interestRate}
          setValue={(v) => setInterestRate(snapInterestRate(v))}
          min={minInterestRate}
          max={maxInterestRate}
          step={interestRateStep}
          format={formatInterestRatePct}
          variant="inr"
          defaultValue={defaults.interestRate}
        />
      </section>

      {!embed ? (
        <section className="emi-input-band emi-input-band--b">
          <EngagerSliderRow
            id="pp-tenure-years"
            label="Original tenure"
            tooltip="Scheduled loan tenure used to size your EMI. Prepayment is assumed to keep this EMI unchanged."
            value={tenureYears}
            setValue={(v) => {
              const next = Math.min(
                maxTenureYears,
                Math.max(minTenureYears, Math.round(Number(v))),
              );
              setTenureYears(next);
            }}
            min={minTenureYears}
            max={maxTenureYears}
            step={1}
            format={formatYearsCount}
            variant="inr"
            defaultValue={defaults.tenureYears}
          />
        </section>
      ) : null}

      <section className="emi-input-band emi-input-band--b">
        <div className="savings-goal-first-year-exp-field">
          <EngagerSliderRow
            id="pp-prepay-amount"
            label="Part-prepayment amount"
            tooltip="Lump-sum paid at the end of the selected year, after that year’s EMIs. EMI stays the same afterward."
            value={Math.min(prepayAmount, loanAmount, maxLoanAmount)}
            setValue={(v) => {
              const next = Math.min(v, loanAmount, maxLoanAmount);
              setPrepayAmount(next);
              setPrepayAmountForWords(next);
            }}
            onLiveValueChange={(n) => {
              if (n != null && Number.isFinite(n)) {
                setPrepayAmountForWords(Math.min(loanAmount, maxLoanAmount, Math.max(0, n)));
              } else {
                setPrepayAmountForWords(0);
              }
            }}
            min={minPrepayAmount}
            max={Math.max(minPrepayAmount, Math.min(loanAmount, maxLoanAmount))}
            step={10000}
            format={(v) => formatterINR.format(v)}
            variant="inr"
            defaultValue={defaults.prepayAmount}
          />
          {prepayAmountWords ? (
            <p className="calc-output-amount-in-words savings-goal-first-year-exp-words">
              {prepayAmountWords}
            </p>
          ) : null}
        </div>
      </section>

      <section className="emi-input-band emi-input-band--b">
        <EngagerSliderRow
          id="pp-prepay-year"
          label="Prepayment timing"
          tooltip="Prepayment is applied at the end of this loan year (after EMIs for that year)."
          value={Math.min(prepayYear, maxPrepayYear)}
          setValue={(v) =>
            setPrepayYear(
              Math.min(maxPrepayYear, Math.max(minPrepayYear, Math.round(Number(v)))),
            )
          }
          min={minPrepayYear}
          max={maxPrepayYear}
          step={1}
          format={formatPrepayYear}
          variant="inr"
          defaultValue={defaults.prepayYear}
        />
      </section>

      {embed ? (
        <section className="emi-input-band emi-input-band--b">
          <EngagerSliderRow
            id="pp-tenure-years-embed"
            label="Original tenure"
            tooltip="Scheduled loan tenure used to size your EMI. Prepayment is assumed to keep this EMI unchanged."
            value={tenureYears}
            setValue={(v) => {
              const next = Math.min(
                maxTenureYears,
                Math.max(minTenureYears, Math.round(Number(v))),
              );
              setTenureYears(next);
            }}
            min={minTenureYears}
            max={maxTenureYears}
            step={1}
            format={formatYearsCount}
            variant="inr"
            defaultValue={defaults.tenureYears}
          />
        </section>
      ) : null}

      {!embed ? (
        <>
          <button type="button" className="calc-start-over-btn" onClick={handleStartOver}>
            Start over
          </button>
          <p className="font-small font-light-grey italic margin-0 nps-ups-footnote">
            Illustrative only: EMI is held constant after a part-prepayment; excludes fees, taxes,
            and lender rules on prepayment. Not advice.
          </p>
        </>
      ) : null}
    </div>
  );

  if (embed) return calculatorCard;

  return (
    <div className="calculator-page-layout">
      {calculatorCard}
      <div className="calculator-rail-shell">
        <CalculatorToolsRail currentToolKey="part-prepayment-calculator" />
      </div>
    </div>
  );
}

function PartPrepaymentResultPanel({ embed = false, impact, prepayYear }) {
  const [showResult, setShowResult] = useState(true);
  const { formatInr } = useCalcAmountDisplayMode();
  const monthsLabel = formatMonthsReduced(impact.monthsReduced);
  const emiDisplay = formatInr(impact.monthlyEmi);
  const wordsLine =
    !embed && impact.monthsReduced > 0
      ? `${impact.monthsReduced} ${impact.monthsReduced === 1 ? 'month' : 'months'} saved`
      : '';

  return (
    <div className="grid-center-display-result nps-ups-result-panel nps-ups-result-outer calc-result-legacy nps-result-legacy">
      <CalculatorPrecisionToggle />
      <button
        type="button"
        className="btn-small-round nps-ups-result-help"
        onClick={() => setShowResult((s) => !s)}
        aria-label={showResult ? 'Show explanation' : 'Show number'}
      >
        {showResult ? '?' : '×'}
      </button>

      {showResult ? (
        <>
          <h4>Tenure reduced by</h4>
          <h1 className="nps-ups-years-figure engager-money-figure part-prepay__months-figure">
            {monthsLabel}
          </h1>
          {wordsLine ? <p className="calc-output-amount-in-words">{wordsLine}</p> : null}
          <p className="part-prepay__emi-line">
            Monthly EMI {emiDisplay}
            {!impact.feasible ? ' · prepayment cannot shorten this loan further' : ''}
          </p>
        </>
      ) : (
        <>
          <h4 className="calc-wtm-heading">What this means</h4>
          <CalculatorWhatThisMeans
            regionLabel="Explanation of part-prepayment tenure reduction"
            heroValue={monthsLabel}
            heroVariant="inr"
            tagline={
              <p>
                <strong>Months cut from the schedule</strong> if you prepay at the{' '}
                {formatPrepayYear(prepayYear).toLowerCase()} and keep paying the same EMI.
              </p>
            }
            highlight={
              <>
                Your EMI stays about <em>{emiDisplay}</em>; the lump sum reduces outstanding
                principal so the loan finishes sooner.
              </>
            }
            highlightTone="warning"
            foot="Illustrative only—not advice."
          />
        </>
      )}
    </div>
  );
}
